import { useEffect, useState } from 'react';
import { Col, Row, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import Button from 'components/new-ui/base/button/Button';
import { JobResponse } from 'store/jobs/job';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import useBreadcrumb from 'hooks/useBreadcrumb';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import moment from 'moment';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import useSession from 'hooks/useSession';
import JobsTable from '../../jobs/components/JobsTable';
import StatusCards from './StatsCards';
import { useRequestPromise } from 'hooks/useRequest';
import { getStatistics } from 'utils/requests/getStatistics';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import ArchiveJobModal from '../components/ArchiveJobModal';
import AssignJobModal from '../components/AssignJobModal';
import UnarchiveJobModal from '../components/UnarchiveJobModal';
import { hasPermissions } from 'config/roles/hasPermissions';
import Notification from 'components/ui/base/notification/Notification';
import { archiveJob, unarchiveJob } from 'store/jobs/client';
import JobFiltersModal from 'views/jobs/components/JobFiltersModal';
import AppliedJobFilters from 'views/jobs/components/AppliedJobFilters';
import { useJobsFilters } from 'views/jobs/useJobsFiters';
import useIndividuals from 'hooks/useIndividuals';
import useCustomers from 'hooks/useCustomers';
import EditJobModal from 'views/jobs/components/edit-job/EditJobModal';

type Modal = 'assign' | 'archive' | 'unarchive' | 'edit' | 'filter' | null;

// Note: CLEARANCE_AGENT default view
const JobsFeed = () => {
    const { userInfo } = useSession();
    const navigate = useNavigate();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { listIndividuals } = useIndividuals();
    const { listCustomers } = useCustomers();

    const {
        data: statsData,
        error: statsError,
        isLoading: isLoadingStats,
        refetch: refetchStatsData,
    } = useRequestPromise(() => getStatistics());

    const [modal, setModal] = useState<Modal>(null);
    const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);

    const canUpdateJobs = hasPermissions(userInfo, 'jobs', 'UPDATE');

    const {
        data,
        isLoading,
        refetch,
        setArchived,
        archived,
        queryInputVal,
        exactDate,
        tags,
        clearFilter,
        clearAll,
        pagination,
        sortDirection,
        sort,
        filters,
        handleFiltersChange,
        handleTableChange,
        handleSearch,
        handleClearSearch,
        handleExactDateChange,
    } =
        // Display only jobs assigned to the CLEARANCE_AGENT
        useJobsFilters((params) => ({ ...params, ownerId: userInfo?.individualId }));

    const closeModal = () => setModal(null);

    const handleArchiveJob = async () => {
        if (!selectedJob?.id) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await archiveJob(selectedJob?.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Archived',
                description: 'Successfully archived job',
            });
            refetch();
            refetchStatsData();
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not archive job',
            });
        } finally {
            hideGlobalOverlay();
            closeModal();
        }
    };

    const handleUnarchiveJob = async () => {
        if (!selectedJob?.id) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await unarchiveJob(selectedJob?.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Unarchived',
                description: 'Successfully Unarchived job',
            });
            refetch();
            refetchStatsData();
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not unarchive job',
            });
        } finally {
            hideGlobalOverlay();
            closeModal();
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Dashboard',
                path: '/home',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    // Refresh the resources in memory
    useEffect(() => {
        listIndividuals({ size: 200 });
        listCustomers({ size: 200 });
    }, [listIndividuals, listCustomers]);

    return (
        <>
            <StyledPageWrapper>
                <Space style={{ width: '100%' }} direction="vertical" size={24}>
                    <PageHeader
                        title="My Jobs"
                        icon={<SuitCaseIcon />}
                        sidebar={
                            <Space size={16}>
                                <ArchiveFilter
                                    value={archived ? 'archived' : 'active'}
                                    onChange={(val) => {
                                        if (val === 'archived') {
                                            setArchived(true);
                                        } else {
                                            setArchived(undefined);
                                        }
                                    }}
                                />
                                <Button type="primary" onClick={() => navigate('/jobs')}>
                                    See All
                                </Button>
                            </Space>
                        }
                    />

                    <StatusCards
                        data={statsData}
                        loading={isLoadingStats}
                        error={statsError}
                        refetch={refetchStatsData}
                    />

                    <Row justify="space-between">
                        <Col>
                            <Space size={8}>
                                <SearchBar
                                    inputPlaceholder="Search jobs"
                                    value={queryInputVal}
                                    onSearch={handleSearch}
                                    onClear={handleClearSearch}
                                />
                                <Button onClick={() => setModal('filter')}>Filter</Button>
                            </Space>
                        </Col>
                        <Col>
                            <DatePicker
                                value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                                onChange={handleExactDateChange}
                            />
                        </Col>
                    </Row>

                    <Row
                        style={{
                            gap: 8,
                        }}
                    >
                        <AppliedJobFilters tags={tags} clearFilter={clearFilter} clearAll={clearAll} />
                    </Row>

                    <JobsTable
                        isUnderManifest
                        jobs={data?.list ?? []}
                        loading={isLoading}
                        actions={{
                            archive: (job) => {
                                setSelectedJob(job);
                                setModal('archive');
                            },
                            unarchive: (job) => {
                                setSelectedJob(job);
                                setModal('unarchive');
                            },
                            assign: (job) => {
                                setSelectedJob(job);
                                setModal('assign');
                            },
                            edit: (job) => {
                                setSelectedJob(job);
                                setModal('edit');
                            },
                        }}
                        onRowClick={(record: JobResponse) => {
                            if (record.manifest?.id) {
                                navigate(`/manifests/${record.manifest.id}/${record.id}`);
                            } else {
                                navigate(`/jobs/${record.id}`);
                            }
                        }}
                        onChange={handleTableChange}
                        pagination={pagination}
                        currentSortDirection={sortDirection}
                        currentSortParam={sort}
                        optionalColumns={{
                            manifestId: true,
                        }}
                    />
                </Space>
            </StyledPageWrapper>

            {canUpdateJobs && selectedJob && (
                <>
                    <AssignJobModal
                        onSuccess={() => {
                            closeModal();
                            refetch();
                        }}
                        open={modal === 'assign'}
                        onClose={closeModal}
                        jobData={{
                            jobId: selectedJob.id,
                            teamId: selectedJob.teamId,
                        }}
                    />
                    <ArchiveJobModal onArchive={handleArchiveJob} open={modal === 'archive'} onClose={closeModal} />
                    <UnarchiveJobModal
                        onUnarchive={handleUnarchiveJob}
                        open={modal === 'unarchive'}
                        onClose={closeModal}
                    />
                    <EditJobModal
                        job={selectedJob}
                        open={modal === 'edit'}
                        onClose={() => {
                            setSelectedJob(null);
                            closeModal();
                        }}
                        onSuccess={refetch}
                    />
                </>
            )}

            <JobFiltersModal
                open={modal === 'filter'}
                onClose={closeModal}
                onClearAll={clearAll}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilter={clearFilter}
                optionalFilters={{
                    customer: true,
                }}
            />
        </>
    );
};

export default JobsFeed;
